import { Component, OnInit, OnDestroy } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { AuthenticationService, CredentialsService } from '../../auth';
import { AuthDialogService } from '../../../services/auth-dialog.service';
import { SearchService } from '../../../services/search-service.service';
import { ProductService } from '../../../services/productservice';

@Component({
  selector: 'app-auth-dialogs',
  templateUrl: './auth-dialogs.component.html',
  styleUrls: ['./auth-dialogs.component.scss'],
  providers: [MessageService],
})
export class AuthDialogsComponent implements OnInit, OnDestroy {
  // Dialog visibility states
  loginDialogue: boolean = false;
  registrationDialog: boolean = false;
  forgotPasswordDialogue: boolean = false;
  profileDialogue: boolean = false;
  changePasswordDialogue: boolean = false;
  resetPasswordDialog: boolean = false;
  userAccountDeleteDialogue: boolean = false;

  // Forms
  loginForm!: FormGroup;
  registerForm: any;
  forgotPassForm: FormGroup;
  changePasswordForm: FormGroup;
  changeUserNameForm: FormGroup;
  accountDeleteForm: FormGroup;
  resetPasswordForm!: FormGroup;

  // Password visibility toggles
  fieldPasswordType: boolean = false;
  fieldOldPasswordType: boolean = false;
  fieldNewPasswordType: boolean = false;
  fieldConfirmPasswordType: boolean = false;
  fieldTempPasswordType: boolean = false;

  // User data
  currentUser: any;
  username: any;
  userid: any;
  userMail: any;
  userCountry: any;
  userProfile: any;
  userLetter: any;

  // UI states
  isLoading = false;
  error: string | undefined;
  showUsername: boolean = false;
  showUserCountry: boolean = false;
  verifiationFailed: boolean = false;
  isSuccessful: boolean;
  isSignUpFailed: boolean;

  // Messages
  confirmMessage: any;
  confirmMessageSummary: any;
  errorMessage: any;

  // Other
  countries: any = [];
  accountDeleteReasons: any[] = [];
  subscribeRes: any;

  // Subscriptions
  private subscriptions: Subscription[] = [];
  public language: string = 'en';

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private formBuilder: FormBuilder,
    private authenticationService: AuthenticationService,
    private credentialsService: CredentialsService,
    private authDialogService: AuthDialogService,
    private messageService: MessageService,
    private translate: TranslateService,
    private searchService: SearchService,
    private productService: ProductService
  ) {
    this.createForm();
    this.createResetForm();
    this.createForgotForm();
    this.createChangePasswordForm();
    this.createRegistrationForm();
    this.createChangeUserNameForm();
    this.createAccountDeleteForm();
  }

  ngOnInit(): void {
    this.language = localStorage.getItem('language') || 'en';
    // Subscribe to dialog state changes
    this.subscriptions.push(
      this.authDialogService.loginDialog$.subscribe((open) => {
        this.loginDialogue = open;
      }),
      this.authDialogService.registrationDialog$.subscribe((open) => {
        this.registrationDialog = open;
      }),
      this.authDialogService.forgotPasswordDialog$.subscribe((open) => {
        this.forgotPasswordDialogue = open;
      }),
      this.authDialogService.profileDialog$.subscribe((open) => {
        this.profileDialogue = open;
        if (open) {
          // Reset edit mode flags when opening
          this.showUsername = false;
          this.showUserCountry = false;
          this.changeUserNameForm.reset();
          this.getUsername();
          this.getUserProfile();
        }
      }),
      this.authDialogService.changePasswordDialog$.subscribe((open) => {
        this.changePasswordDialogue = open;
      }),
      this.authDialogService.resetPasswordDialog$.subscribe((open) => {
        this.resetPasswordDialog = open;
      }),
      this.authDialogService.userAccountDeleteDialog$.subscribe((open) => {
        this.userAccountDeleteDialogue = open;
        if (open) {
          this.authenticationService.getAccountDeleteReasons(this.language).subscribe((res) => {
            this.accountDeleteReasons = res;
          });
        }
      }),
      this.authDialogService.verificationFailed$.subscribe((failed) => {
        this.verifiationFailed = failed;
      })
    );

    // Get countries list
    this.getCountries();

    // Get current user
    this.currentUser = this.authenticationService.currentUserValue;
    if (this.currentUser) {
      this.getUsername();
      this.getUserProfile();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  // Form creation methods
  private createForm() {
    this.loginForm = this.formBuilder.group({
      username: ['', Validators.required],
      password: ['', Validators.required],
      remember: true,
    });
  }

  private createResetForm() {
    this.resetPasswordForm = this.formBuilder.group({
      tempPassword: new FormControl(null, Validators.required),
      password: new FormControl(null, Validators.required),
      confirmPassword: new FormControl(null, Validators.required),
    });
  }

  private createForgotForm() {
    this.forgotPassForm = this.formBuilder.group({
      email: [
        '',
        [
          Validators.required,
          Validators.pattern(
            "^$|^([\\w-']+(?:\\.[\\w-']+)*)@((?:[\\w-]+\\.)*\\w[\\w-]+)\\.([a-zA-Z]+(?:\\.[a-zA-Z]+)?)$"
          ),
        ],
      ],
    });
  }

  private createChangePasswordForm() {
    this.changePasswordForm = this.formBuilder.group(
      {
        newpassword: new FormControl(null, Validators.required),
        oldPassword: new FormControl(null, Validators.required),
        confirmPassword: new FormControl(null, Validators.required),
      },
      {
        validator: this.matchPwds,
      }
    );
  }

  private createRegistrationForm() {
    this.registerForm = this.formBuilder.group({
      mail: new FormControl(null, [
        Validators.required,
        Validators.pattern(
          "^$|^([\\w-']+(?:\\.[\\w-']+)*)@((?:[\\w-]+\\.)*\\w[\\w-]+)\\.([a-zA-Z]+(?:\\.[a-zA-Z]+)?)$"
        ),
      ]),
      name: new FormControl(null, Validators.required),
      pass: new FormControl(null, Validators.required),
      country: new FormControl(null),
      confirm_pass: new FormControl(null, Validators.required),
      agreed: new FormControl('', Validators.required),
      subscribe: new FormControl(null),
    });
  }

  private createChangeUserNameForm() {
    this.changeUserNameForm = this.formBuilder.group({
      newUserName: ['', Validators.required],
      country: ['', Validators.required],
    });
  }

  private createAccountDeleteForm() {
    this.accountDeleteForm = this.formBuilder.group({
      reason: ['', Validators.required],
    });
  }

  // Password visibility toggle methods
  togglePasswordType() {
    this.fieldPasswordType = !this.fieldPasswordType;
  }

  toggleOldPasswordType() {
    this.fieldOldPasswordType = !this.fieldOldPasswordType;
  }

  toggleNewPasswordType() {
    this.fieldNewPasswordType = !this.fieldNewPasswordType;
  }

  toggleConfirmPasswordType() {
    this.fieldConfirmPasswordType = !this.fieldConfirmPasswordType;
  }

  toggleTempPasswordType() {
    this.fieldTempPasswordType = !this.fieldTempPasswordType;
  }

  // Dialog control methods
  closeLoginDialog() {
    this.authDialogService.closeLoginDialog();
    this.registerForm.reset();
    this.loginForm.reset();
  }

  closeForgotPasswordDialog() {
    this.authDialogService.closeForgotPasswordDialog();
  }

  closeRegistrationDialog() {
    this.authDialogService.closeRegistrationDialog();
  }

  closeProfileDialog() {
    this.authDialogService.closeProfileDialog();
  }

  closeChangePasswordDialog() {
    this.authDialogService.closeChangePasswordDialog();
  }

  closeResetPasswordDialog() {
    this.authDialogService.closeResetPasswordDialog();
  }

  closeUserAccountDeleteDialog() {
    this.authDialogService.closeUserAccountDeleteDialog();
  }

  showLoginDialog() {
    this.authDialogService.switchToLogin();
  }

  showRegistrationDialog() {
    this.authDialogService.switchToRegistration();
  }

  showForgotPasswordDialog() {
    this.authDialogService.switchToForgotPassword();
  }

  showUserAccountDeleteDialog() {
    this.authDialogService.openUserAccountDeleteDialog();
  }

  // Login submission
  onLoginSubmit() {
    this.authenticationService.login(this.loginForm.value).subscribe(
      (res: any) => {
        let item = 'temp_pass';
        if (res[item]) {
          this.messageService.add({
            key: 'login',
            severity: 'success',
            summary: this.translate.instant('Success'),
            detail: this.translate.instant('SignIn-Success'),
          });
          setTimeout(() => {
            this.authDialogService.openResetPasswordDialog();
            this.closeLoginDialog();
          }, 1000);
        } else {
          this.messageService.add({
            key: 'login',
            severity: 'success',
            summary: this.translate.instant('Success'),
            detail: this.translate.instant('SignIn-Success'),
          });
          setTimeout(() => {
            this.closeLoginDialog();
            this.currentUser = this.authenticationService.currentUserValue;
            if (this.currentUser) {
              this.getUsername();
              this.getUserProfile();
            }
            if (location.pathname.indexOf('user-activation') > -1) {
              this.router.navigate(['/home']);
            }
          }, 1000);
        }
        localStorage.setItem('token', res.csrf_token);
        if (localStorage.getItem('language') === null) {
          localStorage.setItem('language', 'en');
        }
      },
      (err) => {
        if (err.status === 400) {
          this.messageService.add({
            key: 'login',
            severity: 'error',
            summary: 'Error',
            detail: err.error.message,
          });
        } else {
          console.log(err);
        }
      }
    );
  }

  // Registration submission
  onSubmitRegistrationForm(): void {
    this.isLoading = true;
    const formValues = this.registerForm.value;
    if (formValues.country && formValues.country.id) {
      formValues['field_country'] = [{ country: formValues.country.id, locate: null }];
    }
    this.authenticationService.register(formValues).subscribe(
      (data) => {
        if (formValues.subscribe) {
          const data = {
            email: formValues.mail,
            firstName: formValues.name,
            country: formValues.country.name,
          };
          this.authenticationService.subscribeUser(data).subscribe();
        }
        this.isSuccessful = true;
        this.isLoading = false;
        this.isSignUpFailed = false;
        this.messageService.add({
          key: 'register',
          severity: 'success',
          summary: this.translate.instant('Success'),
          detail: this.translate.instant('Registration-Success'),
        });
        this.registrationDialog = false;
      },
      (err) => {
        this.errorMessage = err.error.message;
        const errorMsgReplace = this.errorMessage.replace('Unprocessable Entity: validation failed.', ' ');
        this.isSignUpFailed = true;
        this.messageService.add({
          key: 'register',
          severity: 'error',
          summary: 'Error',
          detail: errorMsgReplace,
        });
      }
    );
  }

  // Forgot password submission
  onSubmitForgotForm(): void {
    this.authenticationService.forgotPassword(this.forgotPassForm.value).subscribe(
      (res: any) => {
        this.confirmMessage = this.translate.instant('Forgot-Password-Message');
        this.confirmMessageSummary = this.translate.instant('ForgotPWD-MSGSummary');
        this.messageService.add({
          key: 'forgotAlert',
          sticky: true,
          severity: 'success',
          summary: this.confirmMessage,
          detail: this.confirmMessageSummary,
        });
        this.closeForgotPasswordDialog();
      },
      (err) => {
        if (err.status === 400) {
          this.messageService.add({
            key: 'forgot',
            severity: 'error',
            summary: 'Error',
            detail: err.error.message,
          });
        } else {
          this.confirmMessage = this.translate.instant('Forgot-Password-Message');
          this.confirmMessageSummary = this.translate.instant('ForgotPWD-MSGSummary');
          this.messageService.add({
            key: 'forgotAlert',
            sticky: true,
            severity: 'success',
            summary: this.confirmMessage,
            detail: this.confirmMessageSummary,
          });
          this.closeForgotPasswordDialog();
        }
      }
    );
  }

  // Change password submission
  onSubmitChangePassword() {
    this.getUserProfile();
    this.authenticationService.changePassword(this.changePasswordForm.value, this.userProfile).subscribe(
      (res: any) => {
        this.messageService.add({
          key: 'changeuser',
          severity: 'success',
          summary: this.translate.instant('Success'),
          detail: this.translate.instant('Change-Password-Success'),
        });
        setTimeout(() => {
          this.changePasswordDialogue = false;
        }, 2000);
      },
      (err) => {
        this.messageService.add({
          key: 'changeuser',
          severity: 'error',
          summary: 'Error',
          detail: err.error.message,
        });
      }
    );
  }

  // Reset password submission
  onSubmitResetPasswordForm() {
    const currentUserValue = this.authenticationService.currentUserValue;
    if (currentUserValue) {
      this.currentUser = this.authenticationService.currentUser;
      this.authenticationService.resetPassword(this.resetPasswordForm.value, this.currentUser).subscribe(
        (res: any) => {
          this.confirmMessage = this.translate.instant('Change-Password-Success');
          this.confirmMessageSummary = this.translate.instant('Please-Login-ChangePWD-Summary');
          this.messageService.add({
            key: 'c',
            sticky: true,
            severity: 'success',
            summary: this.confirmMessage,
            detail: this.confirmMessageSummary,
          });
          this.closeResetPasswordDialog();
        },
        (err) => {
          this.messageService.add({
            key: 'logout',
            severity: 'error',
            summary: 'Error',
            detail: err.error.message,
          });
        }
      );
    } else {
      this.messageService.add({
        key: 'logout',
        severity: 'error',
        summary: this.translate.instant('Error'),
        detail: this.translate.instant('WrongWindow'),
      });
    }
  }

  // Change username submission
  changeUserName() {
    this.getUserProfile();
    const formValues = this.changeUserNameForm.value;
    if (formValues.country && formValues.country.id) {
      formValues['field_country'] = [{ country: formValues.country.id, locate: null }];
    }
    this.authenticationService.changeUserName(this.changeUserNameForm.value, this.userProfile).subscribe(
      (res: any) => {
        let msg = 'Username and country changed successfully';
        let loginmsg = 'Login with New Username';
        if (
          this.changeUserNameForm.value.newUserName !== '' &&
          !(this.changeUserNameForm.value.country !== '' && this.changeUserNameForm.value.country !== null)
        ) {
          msg = 'Username changed successfully';
        }
        // if (
        //   this.changeUserNameForm.value.newUserName == '' &&
        //   this.changeUserNameForm.value.country !== '' &&
        //   this.changeUserNameForm.value.country !== null
        // ) {
        //   msg = 'Country changed successfully';
        //   loginmsg = 'Login again';
        // }
        if (
          (!this.changeUserNameForm.value.newUserName || this.changeUserNameForm.value.newUserName.trim() === '') &&
          this.changeUserNameForm.value.country
        ) {
          msg = 'Country changed successfully';
          loginmsg = 'Login again';
        }
        console.log({
          newUserName: this.changeUserNameForm.value.newUserName,
          country: this.changeUserNameForm.value.country,
        });
        this.confirmMessage = this.translate.instant(msg);
        this.confirmMessageSummary = this.translate.instant(loginmsg);
        // Reset edit mode flags
        this.showUsername = false;
        this.showUserCountry = false;
        this.changeUserNameForm.reset();
        this.profileDialogue = false;
        this.messageService.add({
          key: 'c',
          sticky: true,
          severity: 'success',
          summary: this.confirmMessage,
          detail: this.confirmMessageSummary,
        });
      },
      (err) => {
        this.messageService.add({
          key: 'changeuser',
          severity: 'error',
          summary: 'Error',
          detail: err.error.message,
        });
      }
    );
  }

  // Delete account
  deleteAccount() {
    const formValues = this.accountDeleteForm.value;
    const req = {
      user_id: this.userid,
      reason: formValues.reason,
    };

    this.authenticationService.deleteAccount(req).subscribe(
      (res: any) => {
        this.profileDialogue = false;
        this.userAccountDeleteDialogue = false;
        this.messageService.add({
          key: 'changeuser',
          severity: 'success',
          summary: this.translate.instant('Success'),
          detail: this.translate.instant('Delete-User-Success'),
        });
        setTimeout(() => {
          this.profileDialogue = false;
          this.userAccountDeleteDialogue = false;
        }, 800);
        this.logout();
      },
      (err) => {
        this.messageService.add({
          key: 'changeuser',
          severity: 'error',
          summary: 'Error',
          detail: err.error.message,
        });
      }
    );
  }

  deleteUserProfile() {
    this.messageService.clear('deleteUserProfile');
    this.getUserProfile();
    this.authenticationService.deleteuserProfile(this.userid).subscribe(
      (res: any) => {
        if (res == null) {
          this.messageService.add({
            key: 'changeuser',
            severity: 'success',
            summary: this.translate.instant('Success'),
            detail: this.translate.instant('Delete-User-Success'),
          });
          setTimeout(() => {
            this.profileDialogue = false;
          }, 800);
          this.logout();
        }
      },
      (err) => {
        this.messageService.add({
          key: 'changeuser',
          severity: 'error',
          summary: 'Error',
          detail: err.error.message,
        });
        setTimeout(() => {
          this.profileDialogue = false;
        }, 2000);
      }
    );
  }

  // User profile methods
  getUserProfile() {
    const userValue = this.authenticationService.currentUserValue;
    this.userid = userValue.current_user.uid;
    this.authenticationService.getUserProfile(this.userid).subscribe(
      (res) => {
        this.userMail = res.mail[0].value;
        if (
          res.field_country &&
          res.field_country[0] &&
          res.field_country[0].country &&
          res.field_country[0].country !== '' &&
          res.field_country[0].country !== null
        ) {
          const country = this.countries.find((val: any) => val.id == res.field_country[0].country);
          if (country) {
            this.userCountry = country.name;
          }
        }
        this.userProfile = res;
      },
      (err) => {
        if (err.error.message === 'Expired token') {
          this.authenticationService.logout().subscribe();
          this.getUsername();
          this.currentUser = this.authenticationService.currentUserValue;
          if (this.currentUser) {
            this.getUserProfile();
          }
        } else if (err.status === 403 && err.error.message && err.error.message.includes('access user profiles')) {
          this.logout();
        }
      }
    );
  }

  getUsername() {
    const userValue = this.authenticationService.currentUserValue;
    if (userValue && userValue.current_user) {
      this.username = userValue.current_user.name;
      this.userLetter = this.getShortName(this.username);
      const credentials = this.credentialsService.credentials;
      return credentials ? credentials.username : null;
    } else {
      this.username = '';
    }
  }

  getShortName(fullName: any) {
    return fullName
      .split(' ')
      .map((n: any[]) => n[0])
      .join('');
  }

  getCountries() {
    this.searchService.getCountries().subscribe((data: any) => {
      this.countries = data.countries_list;
    });
  }

  logout() {
    this.authenticationService.logout().subscribe();
    this.messageService.add({
      key: 'logout',
      severity: 'success',
      summary: this.translate.instant('Success'),
      detail: this.translate.instant('LogOut-Success'),
    });
    this.getUsername();
    this.currentUser = this.authenticationService.currentUserValue;
    if (this.currentUser) {
      this.getUserProfile();
    }
  }

  // Helper methods
  matchPwds(control: AbstractControl) {
    let newPwd2 = control.get('newpassword');
    let confirmPwd2 = control.get('confirmPassword');
    if (newPwd2.value !== confirmPwd2.value) {
      return { pwdsDontMatch: true };
    }
    return null;
  }

  countrySelected() {}

  DisplayUsername() {
    this.showUsername = true;
    this.showUserCountry = false;
  }

  DisplayCountryname() {
    this.showUserCountry = true;
    const selectedCountry = this.countries.find((c: any) => c.name === this.userCountry);
    if (selectedCountry) {
      this.changeUserNameForm.patchValue({ country: selectedCountry });
    }
  }

  onReject() {
    this.messageService.clear('c');
    this.logout();
  }

  onRejectForgotForm() {
    this.messageService.clear('forgotAlert');
  }

  onRejectResetPasswordForm() {
    this.messageService.clear('resetPassword');
    this.authenticationService.logout().subscribe();
    this.logout();
  }

  onRejectDeleteUser() {
    this.messageService.clear('deleteUserProfile');
  }
}
